import GoogleIcon from '@mui/icons-material/Google';
import { Button, ListItemIcon, MenuItem } from '@mui/material';
import { LogOut, Settings } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { initiateGoogleLogin } from '../services/auth/GoogleLogin';
import { SettingsModal } from './SettingsModal';
import sseConsumer from '../utils/ServerSideEventConsumer';
import { UserSettings } from '@/types/types';

interface UserProfileProps {
  isSettingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  isMenuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}

export const isUserSettingsValid = (settings: UserSettings): boolean => {
  const fields = [settings.preferredName, settings.cityState, settings.address, settings.aboutYou];

  const areAllFilled = fields.every((field) => field && field.trim() !== "");
  return areAllFilled;
};

const UserProfile: React.FC<UserProfileProps> = ({ isSettingsOpen, isMenuOpen, setSettingsOpen, setMenuOpen }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, setUser } = useUser();
  const [userInfo, setUserInfo] = useState<UserSettings>({
    preferredName: '',
    cityState: '',
    address: '',
    aboutYou: ''
  });

  const checkGoogleSession = async () => {
    try {
      // Check if we have tokens stored
      const googleToken = localStorage.getItem('googleToken');
      const tokenExpiry = localStorage.getItem('tokenExpiry');

      if (!googleToken || !tokenExpiry) {
        return false;
      }

      // Check if token has expired
      const expiryTime = parseInt(tokenExpiry, 10);
      if (Date.now() >= expiryTime) {
        // Clean up expired tokens
        localStorage.removeItem('googleToken');
        localStorage.removeItem('tokenExpiry');
        return false;
      }
      return true;

      // Optional: Verify token with backend
      // const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-token`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ token: googleToken }),
      //   credentials: 'include'
      // });

      // return response.ok;

    } catch (error) {
      console.error('Error checking Google session:', error);
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/check`, {
          credentials: 'include'
        });
        if (response.ok) {
          const newUser = await response.json();
          setUser(newUser);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      }
    };

    checkAuth();
  }, [setUser]);


  const doGoogleLogin = useCallback(async () => {
    try {
      const googleUser = await initiateGoogleLogin();
      console.log("Google user in UserProfile: ", googleUser);
      if (googleUser) {
        const user = {
          name: googleUser.name,
          email: googleUser.email,
          picture: googleUser.picture,
          hlsStreamUrl: "",
          settings: {
            preferredName: '',
            cityState: '',
            address: '',
            aboutYou: ''
          },
          autoPlay: true,
        };
        setUser(user);
      }
    } catch (error) {
      console.error('Login failed in UserProfile:', error);
    } finally {
      setMenuOpen(false);
    }
  }, [setUser, setMenuOpen]);

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const hasExistingSession = await checkGoogleSession();
        if (hasExistingSession && !isInitialized) {
          await doGoogleLogin();
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Auto-login check failed:', error);
      }
    };

    checkExistingSession();
  }, [isInitialized, doGoogleLogin]);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      setMenuOpen(false);
      sseConsumer.close();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <div className="relative inline-block">
        {user?.picture ? (
          <button
            onClick={() => setMenuOpen(!isMenuOpen)}
            className="max-h-12 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <img src={user.picture} alt={user.name} className="max-h-12 object-cover" />
          </button>
        ) : (
          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={doGoogleLogin}
            sx={{
              backgroundColor: '#4285F4',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#357ABD'
              },
              padding: '8px 16px'
            }}
          >
            Sign in with Google
          </Button>
        )}


        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg" style={{ top: '48px' }}>
            {user ? (
              <>
                <div className="px-4 py-2 border-b border-gray-200">
                  <h3 className="text-lg font-semibold">{user.name}</h3>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>

                <MenuItem onClick={() => {
                  setSettingsOpen(true);
                  setMenuOpen(false);
                }}>
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogOut size={16} className="mr-2" />
                  </ListItemIcon>
                  Sign out
                </MenuItem>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Settings Modal outside the menu dropdown */}
      <SettingsModal
        open={isSettingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={userInfo}
        setSettings={setUserInfo}
      />
    </>
  );
};

export default UserProfile;